import { useFlag } from '@reflag/react-sdk';
import { useQueries, useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    createPalette,
    DateShortRelative,
    PaletteButton,
    PaletteCommandType,
    PalettePositionWidth,
    useDateShortRelative,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Button,
    Description,
    Label,
    LabeledContainer,
    LabeledContainerActionableTitle,
    LabeledContainerBody,
    LabeledContainerHeader,
    List,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
    ListItemSkeleton,
    type ListRenderItemInfo,
    ListSkeleton,
    Tooltip,
    toast,
} from '@gitbook/spine-react';
import { mapSuspenseArray } from '@gitbook/util-suspense-cache';
import type { MaybePromise } from '@gitbook/util-ts';

import { apiClient, apiQuery, apiResource, readAPICache, useLoggedInUser } from '../../state';
import { useSpaceMergeRules } from '../MergeRules';
import { UserAvatar } from '../Users';

export type ChangeRequestPendingRequestedReviewer = {
    kind: 'user';
    userId: string;
};

export type ChangeRequestReviewersListProps = {
    changeRequest: api.ChangeRequest;
    space: api.Space;

    /**
     * Placeholder text to display when no reviewers are selected.
     */
    placeholder?: string;

    /**
     * Whether the list is disabled and non editable.
     */
    disabled?: boolean;

    /**
     * List of pending requested reviewers, not yet synced with the backend.
     */
    pendingRequestedReviewers?: ChangeRequestPendingRequestedReviewer[];

    /**
     * Callback when a change is made to the list of pending requested reviewers.
     */
    onPendingRequestedReviewersChange?: (
        pendingRequestedReviewers: ChangeRequestPendingRequestedReviewer[]
    ) => MaybePromise<void>;
};

/**
 * List the reviewers for a change request.
 */
export function ChangeRequestReviewersList(props: ChangeRequestReviewersListProps) {
    const {
        changeRequest,
        space,
        placeholder = 'No reviewers yet.',
        pendingRequestedReviewers = [],
        onPendingRequestedReviewersChange = async (update) => {
            await apiClient.changeRequestReviewers.requestReviewersForChangeRequest(
                space.id,
                changeRequest.id,
                {
                    users: update.map((reviewer) => reviewer.userId),
                }
            );
        },
    } = props;

    const docsAgentsFeature = useFlag('DOCS_AGENTS');

    const [
        { data: { items: reviews } = { items: [] }, isLoading: isLoadingAllReviews },
        {
            data: { items: requestedReviewers } = { items: [] },
            isLoading: isLoadingRequestedReviewers,
        },
    ] = useQueries({
        queries: [
            apiQuery('getReviewsByChangeRequestId', {
                spaceId: space.id,
                changeRequestId: changeRequest.id,
                limit: 10, // Fetch the top 10 reviews, avoid pagination for now whilst we observe usage
                outdated: false,
            }),
            apiQuery('getRequestedReviewersByChangeRequestId', {
                spaceId: space.id,
                changeRequestId: changeRequest.id,
                limit: 10, // Fetch the top 10 requests, avoid pagination for now whilst we observe usage
            }),
        ],
    });
    const isLoading = isLoadingAllReviews || isLoadingRequestedReviewers;

    //
    // Build the list of suggestions
    //
    const mergeRules = useSpaceMergeRules(space);
    const currentUser = useLoggedInUser();
    const { suggestions, existingReviewers } = React.useMemo(() => {
        const rawSuggestions: ChangeRequestPendingRequestedReviewer[] = [];

        // If the merge rules are set, add the specific reviewers to the list
        if (mergeRules.type === 'rules') {
            mergeRules.rules.forEach((ruleSet) => {
                if (
                    ruleSet.rule === 'require_specific_reviewers' ||
                    ruleSet.rule === 'require_one_of_specific_reviewers'
                ) {
                    ruleSet.users.forEach((userId) => {
                        if (userId === currentUser.uid) {
                            return;
                        }

                        rawSuggestions.push({ kind: 'user', userId });
                    });
                }
            });
        }

        // If the docs agents feature is enabled, add the Gitbook agent to the list
        if (docsAgentsFeature.isEnabled) {
            rawSuggestions.push({ kind: 'user', userId: api.GitBookUsers.GitbookAgent });
        }

        // Remove from the list the reviewers that are already configured.
        const existingReviewers = new Set([
            ...reviews.map((review) => review.reviewer.id),
            ...requestedReviewers.map((reviewer) =>
                reviewer.kind === 'user' ? reviewer.user.id : reviewer.team.id
            ),
            ...(pendingRequestedReviewers
                ? pendingRequestedReviewers.map((reviewer) => reviewer.userId)
                : []),
        ]);
        const suggestions = rawSuggestions.filter((suggestion) => {
            const isExistingReviewer = existingReviewers.has(suggestion.userId);
            existingReviewers.add(suggestion.userId);
            return !isExistingReviewer;
        });

        return { suggestions, existingReviewers };
    }, [
        docsAgentsFeature.isEnabled,
        reviews,
        requestedReviewers,
        pendingRequestedReviewers,
        mergeRules,
        currentUser.uid,
    ]);

    //
    // Palette to add a reviewer
    //
    const addReviewerPalette = () =>
        createPalette({
            search: {
                placeholder: 'Type or choose a user…',
            },
            position: {
                width: PalettePositionWidth.LARGE,
            },
            groups: [
                {
                    key: 'suggestions',
                    title: 'Suggestions',
                    commands: () => {
                        return mapSuspenseArray(suggestions, (suggestion) => {
                            const user = readAPICache(
                                apiResource('getUserById', {
                                    userId: suggestion.userId,
                                })
                            );

                            return {
                                key: suggestion.userId,
                                type: PaletteCommandType.CHECKBOX,
                                selected: false,
                                title: user.displayName,
                                icon: <UserAvatar user={user} size="sm" />,
                                trigger: () => {
                                    return onPendingRequestedReviewersChange([
                                        ...pendingRequestedReviewers,
                                        suggestion,
                                    ]);
                                },
                            };
                        });
                    },
                },
                {
                    key: 'users',
                    title: 'Everyone else',
                    commands: (palette) => {
                        const response = readAPICache(
                            apiResource(
                                'listMembersInOrganizationById',
                                {
                                    organizationId: space.organization,
                                    search: palette.query.text,
                                    limit: 8,
                                },
                                { nullOnError: true }
                            )
                        );
                        if (!response) {
                            return [];
                        }

                        return response.items.map((member) => {
                            const selected = existingReviewers.has(member.user.id);
                            return {
                                key: member.user.id,
                                type: PaletteCommandType.CHECKBOX,
                                selected,
                                disabled: selected,
                                icon: () => (
                                    <UserAvatar user={member.user} size="md" tooltip={false} />
                                ),
                                title: member.user.displayName,
                                subtitle: member.user.email,
                                trigger: () => {
                                    if (selected) {
                                        return;
                                    }

                                    return onPendingRequestedReviewersChange([
                                        ...pendingRequestedReviewers,
                                        { kind: 'user', userId: member.user.id },
                                    ]);
                                },
                            };
                        });
                    },
                },
            ],
        });

    return (
        <LabeledContainer>
            <LabeledContainerHeader>
                <LabeledContainerActionableTitle>
                    <Label>Reviewers</Label>
                    <PaletteButton
                        kind="blank"
                        label="Add and notify reviewers"
                        iconOnly
                        leadingIcon={Icon.Plus}
                        palette={addReviewerPalette}
                    />
                </LabeledContainerActionableTitle>
                {placeholder &&
                requestedReviewers.length === 0 &&
                pendingRequestedReviewers.length === 0 &&
                reviews.length === 0 ? (
                    <Description>{placeholder}</Description>
                ) : null}
            </LabeledContainerHeader>
            <LabeledContainerBody>
                {isLoading ? (
                    <ListSkeleton items={1} />
                ) : (
                    <>
                        {requestedReviewers.length > 0 ||
                        pendingRequestedReviewers.length > 0 ||
                        reviews.length > 0 ? (
                            <div className="flex flex-col">
                                {reviews.length > 0 ? (
                                    <List
                                        items={reviews}
                                        renderItem={({
                                            item,
                                        }: ListRenderItemInfo<api.ChangeRequestReview>) => (
                                            <ChangeRequestReviewItem
                                                space={space}
                                                changeRequest={changeRequest}
                                                review={item}
                                            />
                                        )}
                                    />
                                ) : null}
                                {requestedReviewers.length > 0 ? (
                                    <List
                                        items={requestedReviewers}
                                        renderItem={({
                                            item,
                                        }: ListRenderItemInfo<api.ChangeRequestRequestedReviewer>) => (
                                            <ChangeRequestRequestedReviewerItem
                                                space={space}
                                                changeRequest={changeRequest}
                                                reviewer={item}
                                            />
                                        )}
                                    />
                                ) : null}
                                {pendingRequestedReviewers.length > 0 ? (
                                    <List
                                        items={pendingRequestedReviewers}
                                        renderItem={({
                                            item,
                                        }: ListRenderItemInfo<ChangeRequestPendingRequestedReviewer>) => (
                                            <React.Suspense fallback={<ListItemSkeleton compact />}>
                                                <ChangeRequestPendingRequestedReviewerItem
                                                    suggestion={item}
                                                    isRequested={true}
                                                    onRemove={() => {
                                                        return onPendingRequestedReviewersChange(
                                                            pendingRequestedReviewers.filter(
                                                                (reviewer) =>
                                                                    reviewer.userId !== item.userId
                                                            )
                                                        );
                                                    }}
                                                />
                                            </React.Suspense>
                                        )}
                                    />
                                ) : null}
                            </div>
                        ) : null}

                        {suggestions.length > 0 && !isLoading ? (
                            <List
                                items={suggestions}
                                renderItem={({
                                    item,
                                }: ListRenderItemInfo<ChangeRequestPendingRequestedReviewer>) => (
                                    <React.Suspense fallback={<ListItemSkeleton compact />}>
                                        <ChangeRequestPendingRequestedReviewerItem
                                            suggestion={item}
                                            isRequested={false}
                                            onRequest={() => {
                                                return onPendingRequestedReviewersChange([
                                                    ...pendingRequestedReviewers,
                                                    item,
                                                ]);
                                            }}
                                            onRemove={() => {
                                                return onPendingRequestedReviewersChange(
                                                    pendingRequestedReviewers.filter(
                                                        (reviewer) =>
                                                            reviewer.userId !== item.userId
                                                    )
                                                );
                                            }}
                                        />
                                    </React.Suspense>
                                )}
                            />
                        ) : null}
                    </>
                )}
            </LabeledContainerBody>
        </LabeledContainer>
    );
}

/**
 * Item for a review.
 */
function ChangeRequestReviewItem(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    review: api.ChangeRequestReview;
}) {
    const { space, changeRequest, review } = props;

    const { value: humanPeriod } = useDateShortRelative(review.updatedAt, { onlyDate: false });

    return (
        <ListItem compact>
            <ListItemIndicator>
                <UserAvatar user={review.reviewer} tooltip={false} size="md" />
            </ListItemIndicator>
            <ListItemBody>
                <ListItemDescription>{review.reviewer.displayName}</ListItemDescription>
                <ListItemDetail>
                    {review.status === api.ChangeRequestReviewStatus.Approved
                        ? 'Approved'
                        : 'Changes requested'}{' '}
                    <DateShortRelative value={review.updatedAt} onlyDate={false} />
                </ListItemDetail>
            </ListItemBody>
            <ListItemControls>
                <Button
                    kind="blank"
                    iconOnly
                    size="small"
                    leadingIcon={Icon.Refresh}
                    label="Re-request review"
                    onPress={() => {
                        return reRequestReview({
                            spaceId: space.id,
                            changeRequestId: changeRequest.id,
                            userId: review.reviewer.id,
                        });
                    }}
                />
            </ListItemControls>
            <ListItemControls when="always">
                {review.status === api.ChangeRequestReviewStatus.Approved ? (
                    <Tooltip
                        label={`${review.reviewer.displayName} approved this change request ${humanPeriod}`}
                    >
                        <Badge
                            size="xsmall"
                            kind="done"
                            iconOnly
                            icon={Icon.Check}
                            label="Approved"
                            className="mr-1"
                        />
                    </Tooltip>
                ) : (
                    <Badge
                        size="xsmall"
                        kind="danger"
                        iconOnly
                        icon={Icon.X}
                        label="Changes requested"
                        className="mr-1"
                    />
                )}
            </ListItemControls>
        </ListItem>
    );
}

/**
 * Item for a requested reviewer.
 */
function ChangeRequestRequestedReviewerItem(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    reviewer: api.ChangeRequestRequestedReviewer;
}) {
    const { space, changeRequest, reviewer } = props;

    if (reviewer.kind !== 'user') {
        return null;
    }

    return (
        <ListItem compact>
            <ListItemIndicator>
                <UserAvatar user={reviewer.user} tooltip={false} size="md" />
            </ListItemIndicator>
            <ListItemBody>
                <ListItemDescription>{reviewer.user.displayName}</ListItemDescription>
                <ListItemDetail>
                    Requested <DateShortRelative value={reviewer.createdAt} onlyDate={false} />
                </ListItemDetail>
            </ListItemBody>
            <ListItemControls>
                <PaletteButton
                    kind="blank"
                    size="small"
                    label="Edit reviewer"
                    iconOnly
                    leadingIcon={Icon.MoreHorizontal}
                    palette={() =>
                        createPalette({
                            groups: [
                                {
                                    key: 'actions',
                                    commands: [
                                        {
                                            key: 're-request',
                                            title: 'Re-request review',
                                            icon: Icon.Refresh,
                                            trigger: async () => {
                                                await reRequestReview({
                                                    spaceId: space.id,
                                                    changeRequestId: changeRequest.id,
                                                    userId: reviewer.user.id,
                                                });
                                            },
                                        },
                                        {
                                            key: 'remove',
                                            title: 'Remove',
                                            icon: Icon.Trashcan,
                                            trigger: async () => {
                                                await apiClient.changeRequestReviewers.removeRequestedReviewerFromChangeRequest(
                                                    space.id,
                                                    changeRequest.id,
                                                    reviewer.user.id
                                                );
                                            },
                                        },
                                    ],
                                },
                            ],
                        })
                    }
                />
            </ListItemControls>
            <ListItemControls when="always">
                <Tooltip label={`Awaiting requested review from ${reviewer.user.displayName}`}>
                    <Badge
                        size="xsmall"
                        kind="warning"
                        iconOnly
                        icon={Icon.Dot}
                        label="Awaiting"
                        className="mr-1"
                    />
                </Tooltip>
            </ListItemControls>
        </ListItem>
    );
}

/**
 * Item that is not yet synced in the backend.
 */
function ChangeRequestPendingRequestedReviewerItem(props: {
    suggestion: ChangeRequestPendingRequestedReviewer;
    isRequested?: boolean;
    onRequest?: () => MaybePromise<void>;
    onRemove?: () => MaybePromise<void>;
}) {
    const { suggestion, isRequested, onRequest, onRemove } = props;
    const { data: user } = useSuspenseQuery(apiQuery('getUserById', { userId: suggestion.userId }));

    return (
        <ListItem compact>
            <ListItemIndicator>
                <UserAvatar user={user} tooltip={false} size="md" />
            </ListItemIndicator>
            <ListItemBody>
                <ListItemDescription>{user.displayName}</ListItemDescription>
                <ListItemDetail>Suggested</ListItemDetail>
            </ListItemBody>
            <ListItemControls when="always">
                <Button
                    kind="blank"
                    leadingIcon={isRequested ? Icon.Check : Icon.Bell}
                    label={isRequested ? 'Requested' : 'Request'}
                    onPress={() => {
                        if (isRequested) {
                            return onRemove?.();
                        } else {
                            return onRequest?.();
                        }
                    }}
                />
            </ListItemControls>
        </ListItem>
    );
}

async function reRequestReview(props: {
    spaceId: string;
    changeRequestId: string;
    userId: string;
}) {
    const { spaceId, changeRequestId, userId } = props;

    await toast.catch(async () => {
        await apiClient.changeRequestReviewers.requestReviewersForChangeRequest(
            spaceId,
            changeRequestId,
            { users: [userId] }
        );

        toast.success('Review re-requested, the reviewer will be notified.');
    }, 'Error while re-requesting review');
}
